// @flow
/** @jsx jsx */

import _ from 'lodash';
import { useState } from 'react';

import { jsx } from '@emotion/core';
import { Colors, Duration } from '@zapier/theme';

import Link from 'app/common/components/Link';
import Popover from 'app/common/components/Popover';
import Tag from 'app/common/components/Tag';
import TooltipHover from 'app/common/components/TooltipHover';
import purify from 'app/common/components/purify';

const Styles = {
  appTitle: {
    fontWeight: 600,
    color: Colors.batmobile,
    fontSize: '0.9em',
  },
  appListTag: {
    position: 'absolute',
    margin: '0 auto',
    left: 0,
    right: 0,
    bottom: '94%',
    height: '18px',
    padding: 0,
    width: '90px',
  },
  serviceDescription: {
    textAlign: 'left',
    lineHeight: 'normal',
    div: {
      marginTop: '1em',
    },
  },
  service: isActive => ({
    opacity: isActive ? 1 : 0.3,
  }),
};

const IconWithTag = purify('IconWithTag', props => {
  // if the tag is upcoming the corresponding logo comes as a parsedURL string >:[
  if (props.tagClass === 'upcoming' || props.tagClass === 'early-access') {
    const logoStyle = {
      backgroundImage: `url(${props.icon})`,
      backgroundSize: '54px',
      backgroundRepeat: 'no-repeat',
    };
    return (
      <div>
        <Tag css={Styles.appListTag} kind={`${props.tagClass} small`}>
          {props.tagClass.replace('-', ' ')}
        </Tag>
        <div className="app-logo" style={logoStyle} />
      </div>
    );
    // beta and upcoming tags consistently keep the app logo in their className
  } else {
    return (
      <div>
        <Tag css={Styles.appListTag} kind={`${props.tagClass} small`}>
          {props.tagClass}
        </Tag>
        <div className={props.icon} />
      </div>
    );
  }
});

const AppListItemInner = purify('AppListItemInner', props => (
  <Popover kind="fade-in" onMove={props.hideDescription}>
    <TooltipHover
      delay={Duration.tooltipTimeout.short}
      onEnter={props.showDescription}
      onLeave={props.hideDescription}
    >
      <Link
        href={props.path}
        onBlur={props.hideDescription}
        onFocus={props.showDescription}
      >
        <div>
          {props.hasTag ? (
            <IconWithTag {...props} />
          ) : (
            <div className={props.icon} />
          )}
        </div>
        <span css={Styles.appTitle}>{props.service}</span>
      </Link>
    </TooltipHover>
    {props.shouldShowDescription ? (
      <div
        css={Styles.serviceDescription}
        dangerouslySetInnerHTML={{
          __html: _.get(props, ['dataset', 'description']),
        }}
      />
    ) : null}
  </Popover>
));

type Props = {
  className: string,
  dataset: { [string]: string },
  hasTag: boolean,
  isActive: boolean,
  icon: string,
  id: number,
  path: string,
  service: string,
  serviceTypes: { [string]: boolean },
  tagClass?: string,
};

const AppListItem = (props: Props) => {
  const [shouldShowDescription, setShouldShowDescription] = useState(false);

  const { isActive, ...rest } = props;
  return (
    <li className={props.className} css={Styles.service(isActive)}>
      <AppListItemInner
        hideDescription={() => setShouldShowDescription(false)}
        shouldShowDescription={shouldShowDescription}
        showDescription={() => setShouldShowDescription(true)}
        {...rest}
      />
    </li>
  );
};

export default AppListItem;
